import React from "react";

const Team: React.FC = () => {
  const teamMembers = [
    {
      name: "Benjamin Acquah",
      role: "Chief Executive Officer",
      image: "/ben.png",
      description:
        "With over 8 years of experience in manufacturing and fabrication, Benjamin leads our company with vision and expertise.",
      socialLinks: {
        linkedin: "#",
        twitter: "#",
      },
    },
    {
      name: "Ebenezer Ato Acquah",
      role: "Head of Operations",
      image: "/eben.jpg",
      description:
        "Ebenezer oversees all operational aspects, ensuring efficient project delivery and maintaining high quality standards.",
      socialLinks: {
        linkedin: "#",
        twitter: "#",
      },
    },
    {
      name: "Mike Johnson",
      role: "Lead Engineer",
      image: "/lead_eng.jpg",
      description:
        "Mike brings technical excellence to our projects with his extensive experience in industrial engineering.",
      socialLinks: {
        linkedin: "#",
        twitter: "#",
      },
    },
    {
      name: "Sarah Wilson",
      role: "Quality Control Manager",
      image: "/quality.jpg",
      description:
        "Sarah ensures all our products meet the highest quality standards through rigorous testing and inspection.",
      socialLinks: {
        linkedin: "#",
        twitter: "#",
      },
    },
  ];

  const certifications = [
    "Certified Welding Inspector (CWI)",
    "RGD Certification",
    "Safety Management Certification",
    "Quality Control Certification",
  ];

  return (
    <div className="bg-white">
      {/* Hero Section */}
      <section className="relative bg-gray-900 text-white py-24">
        <div className="absolute inset-0">
          <img
            src="/about.jpg"
            alt="Our Team"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black opacity-60"></div>
        </div>
        <div className="relative container mx-auto px-4">
          <h1 className="text-5xl font-bold mb-6">Our Team</h1>
          <p className="text-lg text-gray-300 max-w-3xl">
            Meet the dedicated professionals behind Ekumfi Engineering's
            success. Our team combines expertise, passion, and commitment to
            deliver excellence in every project.
          </p>
        </div>
      </section>

      {/* Team Members Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">Meet Our Experts</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Our team of skilled professionals brings together years of
              experience and expertise in manufacturing and fabrication.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {teamMembers.map((member, index) => (
              <div
                key={index}
                className="bg-white rounded-lg shadow-lg overflow-hidden group"
              >
                <div className="relative">
                  <img
                    src={member.image}
                    alt={member.name}
                    className="w-full h-64 object-cover transition-transform duration-300 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-black opacity-0 group-hover:opacity-40 transition-opacity duration-300"></div>
                  <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="flex space-x-4 justify-center">
                      <a
                        href={member.socialLinks.linkedin}
                        className="text-white hover:text-orange-500"
                      >
                        <svg
                          className="w-6 h-6"
                          fill="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" />
                        </svg>
                      </a>
                      <a
                        href={member.socialLinks.twitter}
                        className="text-white hover:text-orange-500"
                      >
                        <svg
                          className="w-6 h-6"
                          fill="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                        </svg>
                      </a>
                    </div>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold mb-2">{member.name}</h3>
                  <p className="text-orange-500 text-sm mb-4">{member.role}</p>
                  <p className="text-gray-600 text-sm">{member.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Certifications Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">Our Certifications</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              We maintain the highest standards through professional
              certifications and continuous training.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {certifications.map((cert, index) => (
              <div
                key={index}
                className="bg-white p-6 rounded-lg shadow-lg text-center"
              >
                <div className="text-orange-500 mb-4">
                  <svg
                    className="w-12 h-12 mx-auto"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z"
                    />
                  </svg>
                </div>
                <h3 className="text-lg font-bold mb-2">{cert}</h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Join Our Team Section */}
      <section className="py-16 bg-gray-900 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-4xl font-bold mb-6">Join Our Team</h2>
            <p className="text-gray-300 mb-8">
              We're always looking for talented individuals to join our team. If
              you're passionate about manufacturing and fabrication, we'd love
              to hear from you.
            </p>
            <button className="bg-orange-500 text-white px-8 py-3 rounded hover:bg-orange-600 transition-colors">
              View Open Positions
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Team;
